import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import TrashIcon from '~/assets/icons/trash.svg';
import EditIcon from '~/assets/icons/edit.svg';
import PreviewIcon from '~/assets/icons/eye.svg';
import PlayIcon from '~/assets/icons/sm_play.svg';
import { useEditorStore } from '~/shell/Store';
import ConfirmModal from './ConfirmModal';

export const TrashIconForButton = styled(TrashIcon)``;
export const EditIconForButton = styled(EditIcon)``;
export const PreviewIconForButton = styled(PreviewIcon)``;
export const PlayIconForButton = styled(PlayIcon)``;

const PopupContainer = styled.div`
  position: absolute;
  background-color: #ffffff;
  border: 1px solid #ccc;

  box-shadow: 0px 8px 9px 0px #00000036;
  box-shadow: 0px 30px 30px 0px #00000008;
  box-shadow: 0px -2px 4px 0px #0000000d;

  padding: 5px 0px 0px 0px;
  gap: 0px;
  border-radius: 5px;
  opacity: 0px;

  z-index: 1000;
  flex-direction: column;
  display: flex;
  margin-top: 10px;
  margin-left: -20px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  margin: 3px;
  padding: 7px;
  background-color: #fff;

  font-family: Raleway;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  white-space: nowrap;

  svg {
    margin-right: 5px;
  }

  &:hover {
    background-color: #f5f5f5;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

interface PopupMenuProps {
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  onClose: () => void;
  data: any;
  training: any;
  setTraining: (data: any) => void;
  apiService: any;
  id: string;
  isDeleteDisabled?: boolean;
}

const PopupMenuForChapter: React.FC<PopupMenuProps> = ({
  isOpen,
  anchorElement,
  onClose,
  data,
  training,
  id,
  isDeleteDisabled
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const { setTraining, apiService, sequence, setSequence } = useEditorStore();
  const [disableDelete, setDisableDelete] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (anchorElement) {
      const rect = anchorElement.getBoundingClientRect();

      let top = rect.bottom - 30;
      const left = rect.left - 70;

      if (top + 100 >= window.innerHeight) {
        top = top - 100;
      }

      if (top < 0) {
        top = 0;
      }

      setPosition({ top, left });
    }
  }, [anchorElement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        anchorElement &&
        !anchorElement.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.popup-container') &&
        !(event.target as HTMLElement).closest('.modal-container') &&
        !showConfirmModal
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, anchorElement, onClose, showConfirmModal]);

  useEffect(() => {
    const currentChapterSequencesCount = training.chapters.find(
      (chapter: any) => chapter.id == id
    ).chapterSequences.length;
    const totalChapterSequences = training.chapters.reduce(
      (acc: number, chapter: any) => {
        return acc + chapter.chapterSequences.length;
      },
      0
    );

    const isDeleteDisabled =
      training.chapters.length <= 1 ||
      (currentChapterSequencesCount > 0 &&
        currentChapterSequencesCount == totalChapterSequences);

    setDisableDelete(isDeleteDisabled);
  }, [training, id]);

  if (!isOpen || !anchorElement) return null;

  const renameClickHandler = () => {
    const updatedChapters = training.chapters.map((chapter: any) => {
      if (chapter.id == data.id) {
        return { ...chapter, _rename: true };
      } else {
        if (chapter._rename) {
          delete chapter._rename;
        }
        return chapter;
      }
    });

    setTraining({ ...training, chapters: updatedChapters });
    onClose();
  };

  const deleteClickHandler = () => {
    if (popupRef && popupRef.current) {
      popupRef.current.style.display = 'none';
    }
    setShowConfirmModal(true);
  };

  const deleteChapter = () => {
    if (apiService) {
      apiService.deleteChapter(data.id);
    }

    const updatedChapters = training.chapters.filter(
      (chapter: any) => chapter.id != data.id
    );
    const newTraining = { ...training, chapters: updatedChapters };
    onClose();

    const sequenceExists = newTraining.chapters.some((chapter: any) =>
      chapter.chapterSequences.some(
        (seq: any) => seq.sequence.id == sequence.id
      )
    );

    if (!sequenceExists) {
      const firstSequence = newTraining.chapters[0].chapterSequences[0];
      setSequence(firstSequence.sequence);
    }

    setTraining(newTraining);
    setShowConfirmModal(false);
  };

  const handleConfirmDelete = () => {
    deleteChapter();
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    onClose();
  };

  return (
    <>
      {isOpen &&
        !showConfirmModal &&
        ReactDOM.createPortal(
          <PopupContainer
            className="popup-container"
            style={{ top: position.top, left: position.left }}
            ref={popupRef}
          >
            <Button onClick={renameClickHandler}>
              <EditIconForButton /> Rename
            </Button>
            <Button onClick={deleteClickHandler} disabled={isDeleteDisabled}>
              <TrashIconForButton
                style={{ marginLeft: '-5px', fill: '#2E2E2E' }}
              />{' '}
              Delete
            </Button>
          </PopupContainer>,
          document.body
        )}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmDelete}
        title="Delete Chapter"
        message="Are you sure you want to delete this chapter? This action cannot be undone."
      />
    </>
  );
};

export default PopupMenuForChapter;
